import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {environment} from 'src/environments/environment';
import {TrustiiService} from 'src/app/services/trustii.service';
import {DataService, DatasetBannerModel, DatasetBannerView} from 'src/app/services/data.service';
// Rxjs
import {Observable, of, throwError} from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {KeyValue} from '@angular/common';
import { DialogService } from 'src/app/services/dialog.service';

@Component({
  selector: 'app-vendor-predictions',
  templateUrl: './predictions-list.component.html',
  styleUrls: ['./predictions-list.component.scss']
})
export class PredictionsListComponent implements OnInit {
  private params: Params;
  datasetId = 0;
  dataset: any = false;
  isReady = false;
  devMode = false;
  bestValidatedScore: any;
  bestPrivateValidatedScore: any;
  elements: any = [];
  nbSelected = 0;
  meta: any = {
    total: 0,
    nb_page: 1,
    current_page: 1,
    first_page: 1,
    last_page: 1,
    count: 0,
    per_page: 20,
  };

  constructor(private router: Router,
              private route: ActivatedRoute,
              private trustiiService: TrustiiService,
              public dataservice: DataService,
              private dialogService: DialogService,
              public datasetBannerModel: DatasetBannerModel) {
  }

  /**
   * Init component
   *
   * @return void
   */
  ngOnInit(): void {
    this.datasetBannerModel.setBannerView(DatasetBannerView.PredictionView);
    this.devMode = !environment.production;

    this._loadParams$()
      .pipe(
        switchMap(this._loadDataset$.bind(this)),
        tap(this.getPredictions.bind(this))
      )
      .subscribe();
  }

  /** _loadParams$ method
   *
   * Load params and set class properties.
   * @return Observable<Params>
   */
  private _loadParams$(): Observable<Params> {
    return this.route.params
      .pipe(
        map((params: Params) => this.params = params)
      );
  }

  /** _loadDataset$ method
   *
   * Load dataset if there is id dataset on params.
   */
  private _loadDataset$(): Observable<any> {
    if (!this.params.datasetId || this.params.datasetId <= 0) {
      return of({});
    }
    this.datasetId = this.params.datasetId;
    return this.trustiiService.getDataset(this.datasetId)
      .pipe(
        map((dataset: any) => this.dataset = dataset.data),
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  /**
   * Get predictions
   *
   * @return void
   */
  getPredictions(): void {
    this.trustiiService.getVendorPredictions(this.datasetId, this.meta.page, this.meta.per_page).subscribe(
      (res) => {
        this.getBestPrediction(res.data);
        res.data.forEach((prediction: any) => {
          if (prediction.selected === 'yes') {
            this.nbSelected++;
          }
        });
        this.elements = res.data;
        this.meta = res.meta;
        this.isReady = true;
      },
      (err) => {
        this.isReady = true;
        this.elements = [];
      }
    );
  }

  getBestPrediction(predictions: any[]): void {
    if (predictions.length > 0) {
      const validatedElements = predictions.filter(value => value.selected === 'yes');
      // Best private
      if (!this.dataset.endDate || (this.dataset.endDate && this.dataservice.isEndDateExpired(this.dataset.endDate))) {
        const privateData = validatedElements.sort((a, b) => {
          if (b.defaultPrivateScore === a.defaultPrivateScore) {
            return a.id - b.id;
          }
          return b.defaultPrivateScore - a.defaultPrivateScore;
        });
        this.bestPrivateValidatedScore = (privateData.length > 0) ? privateData[0] : null;
      }
      // Best public
      const data = validatedElements.sort((a, b) => {
        if (b.defaultPublicScore === a.defaultPublicScore) {
          return a.id - b.id;
        }
        return b.defaultPublicScore - a.defaultPublicScore;
      });
      this.bestValidatedScore = (data.length > 0) ? data[0] : null;
    }
  }

  selectPrediction(id: number, selected: string) {
    this.trustiiService.updatePrediction(id, { selected: (selected === 'yes') ? 'no' : 'yes' }).subscribe(
      (res) => {
        let nbSelected = 0;
        this.elements = this.elements.map((prediction: any) => {
          if (prediction.id === res.data.id) {
            prediction.selected = res.data.selected;
          }
          if (prediction.selected === 'yes') {
            nbSelected++;
          }
          return prediction;
        });
        console.log(nbSelected);
        this.nbSelected = nbSelected;
        this.getBestPrediction(this.elements);
      },
      (err) => {
        // add snippet
      }
    );
  }

  showError(prediction: any): void {
    this.dialogService.openAlertDialog(
      '350px',
      prediction.error
        ? prediction.error
        : 'An error occured during prediction analysis.',
      'Detail of the error',
      'Close',
      'modal-warning'
    );
  }

  /**
   * When limit changed
   *
   * @param perPage Nb result per page
   * @return void
   */
  onLimitChanged(perPage: number): void {
    this.meta.per_page = perPage;
    this.getPredictions();
  }

  /**
   * When page changed
   *
   * @param page The page
   * @return void
   */
  onPageChanged(page: number): void {
    this.meta.page = page;
    this.getPredictions();
  }

  /**
   * Format date
   *
   * @param stringDate The date
   * @return Local date
   */
  formatDate(stringDate: string): string {
    const date = new Date(stringDate);
    return date.toLocaleString();
  }

  // Order by descending property value
  sortByValue = (a: KeyValue<string, number>, b: KeyValue<string, number>): number => {
    return b.value - a.value;
  }
}
